import { useMemo } from 'react';
import { PermissionType, useGetTeletriageUnreadMessageCountQuery } from 'shared/types/graphql';
import { cache } from 'shared/providers/ApolloProvider/cache';
import { GraphQLFetchPolicies } from 'shared/enums';
import useInAppNotifications from 'shared/hooks/useInAppNotifications';
import { useTeletriageClinicData } from './useTeletriageClinicData';
import useClinicUser from 'shared/hooks/useClinicUser';

interface UseTeletriageUnreadMessagesOutput {
  count: number;
  unreadChannelIds: string[];
  refetchTeletriageUnreadMessageCountData: () => void;
}

export const useTeletriageUnreadMessages = (): UseTeletriageUnreadMessagesOutput => {
  const { userHasPermission } = useClinicUser();
  const { careClinicsIds: clinicIds, isLoading, teletriageStatusNames: statusNames } = useTeletriageClinicData();

  const canManageTeletriage = useMemo(() => userHasPermission(PermissionType.ManageTeletriage), [userHasPermission]);

  const { data: teletriageUnreadMessageCountData, refetch: refetchTeletriageUnreadMessageCountData } =
    useGetTeletriageUnreadMessageCountQuery({
      variables: {
        clinicIds,
      },
      skip: !clinicIds?.length || isLoading || !canManageTeletriage,
      fetchPolicy: GraphQLFetchPolicies.CacheFirst,
      notifyOnNetworkStatusChange: true,
      onCompleted: () => {
        /**
         * Explicitly invalidate the cache when results return from the network
         * https://github.com/apollographql/apollo-client/issues/7994
         */
        cache.modify({
          id: cache.identify({ __typename: 'TeletriageChannelUnreadMessageCountResult' }),
          fields(fieldValue, details) {
            return details.INVALIDATE;
          },
        });
      },
    });

  useInAppNotifications({
    clinicIds,
    refreshData: canManageTeletriage ? refetchTeletriageUnreadMessageCountData : undefined,
    isTeletriage: true,
    teletriageStatusNames: statusNames,
    canManageTeletriage,
  });

  const count = useMemo(
    () => teletriageUnreadMessageCountData?.teletriageChannelUnreadMessageCount?.client?.total || 0,
    [teletriageUnreadMessageCountData?.teletriageChannelUnreadMessageCount?.client?.total],
  );

  const unreadChannelIds = useMemo(() => {
    const _unreadChannels =
      teletriageUnreadMessageCountData?.teletriageChannelUnreadMessageCount?.client?.unreadChannels;
    return _unreadChannels?.map((channel) => channel.channelId) || [];
  }, [teletriageUnreadMessageCountData?.teletriageChannelUnreadMessageCount?.client?.unreadChannels]);

  return {
    count,
    unreadChannelIds,
    refetchTeletriageUnreadMessageCountData,
  };
};
